import * as React from 'react';
import moment from 'moment-timezone';
import FullCalendar, {
  DateSelectArg,
  EventClickArg,
} from '@fullcalendar/react';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid'; // a plugin!
import timeGridPlugin from '@fullcalendar/timegrid';
import { withStyles } from '@material-ui/core';
import { connect } from 'react-redux';

import TourDialog from './TourDialog';
import ShowingsNotification from './ShowingSnackbar';
import OpenHouseDialog from './OpenHouseDialog';
import {
  frameCalendarEvents,
  frameOpenHouseEvents,
  StartDate,
  MaxDate,
} from '../../../lib/helpers';
import {
  deleteShowing,
  submitShowingRequest,
  updateShowing,
} from '../../../actions/showing';
import styles from './styles';
import { emitNotificationEvent } from '../../../actions/auth';

interface Props {
  classes: any;
  dispatch: any;
  showings: any;
  openHouses?: any;
  containerHeight?: number;
  disableSelect?: boolean;
  token: string;
  listing?: any;
}

interface State {
  displayOpenHouseDialog: boolean;
  isEditing: boolean;
  isOpenHouse: boolean;
  openTourDialog: boolean;
  selectedDate: Date | undefined;
  selectedOpenHouse: any;
  selectedShowing: any;
}

export class ShowingsCalendar extends React.Component<Props, State> {
  calendarRef: any;
  static defaultProps = {
    openHouses: [],
  };
  constructor(props: Props) {
    super(props);
    this.calendarRef = React.createRef();
    this.state = {
      displayOpenHouseDialog: false,
      isEditing: false,
      isOpenHouse: false,
      openTourDialog: false,
      selectedDate: undefined,
      selectedOpenHouse: undefined,
      selectedShowing: undefined,
    };
  }

  handleClose = () => {
    this.setState({ openTourDialog: false, displayOpenHouseDialog: false });
  };

  handleDateClick = (arg: DateSelectArg) => {
    const { start } = arg;
    this.setState({
      isEditing: false,
      openTourDialog: true,
      selectedDate: start,
    });
  };

  handleEventClick = (event: EventClickArg) => {
    const {
      showingDetails,
      openHouseDetails,
      isOlderEvent,
    } = event.event._def.extendedProps;

    if (isOlderEvent) {
      return;
    }
    if (openHouseDetails) {
      const date = new Date(openHouseDetails.start);
      this.setState({
        displayOpenHouseDialog: true,
        isEditing: false,
        selectedDate: date,
        selectedOpenHouse: openHouseDetails,
      });
      return;
    }
    const date = new Date(showingDetails.date);
    if (showingDetails.isOpenHouse) {
      this.setState({
        displayOpenHouseDialog: true,
        isEditing: true,
        selectedDate: date,
        selectedShowing: showingDetails,
      });
    } else {
      this.setState({
        isEditing: true,
        openTourDialog: true,
        selectedDate: date,
        selectedShowing: showingDetails,
      });
    }
  };

  formatFinalDateTime = (time: moment.Moment) => {
    const day = moment(moment(this.state.selectedDate).startOf('day'))
      .startOf('day')
      .format('MM/DD/YYYY');
    const combinedDate = moment(
      `${day} ${moment(time).format('HH:mm A')}`,
      'MM/DD/YYYY HH:mm A',
    ).format('YYYY-MM-DD[T]HH:mm:ss');
    return combinedDate;
  };

  handleSuccessRequest = () => {
    this.setState({
      openTourDialog: false,
      displayOpenHouseDialog: false,
    });
  };

  handleScheduleShowing = (time: moment.Moment) => {
    const date = this.formatFinalDateTime(time);
    const { listing, token } = this.props;
    const values: any = {
      date,
      listingId: listing.id,
      timezone: moment.tz.guess(),
    };

    this.props.dispatch(
      submitShowingRequest(values, token, () => this.handleSuccessRequest()),
    );
  };

  handleUpdateShowing = (time: moment.Moment) => {
    const date = this.formatFinalDateTime(time);
    const values: any = {
      timezone: moment.tz.guess(),
      date,
    };
    const showingId = this.state.selectedShowing.id;
    this.props.dispatch(
      updateShowing(showingId, values, this.props.token, () =>
        this.handleSuccessRequest(),
      ),
    );
  };

  handleDeleteShowing = (isOpenHouse: boolean = false) => {
    const showingId = this.state.selectedShowing.id;
    this.props.dispatch(
      deleteShowing(
        showingId,
        this.props.token,
        () => this.handleSuccessRequest(),
        isOpenHouse,
      ),
    );
  };

  handleOpenHouseVisit = () => {
    const date = moment(this.state.selectedDate).format(
      'YYYY-MM-DD[T]HH:mm:ss',
    );
    const { listing, token } = this.props;
    const values: any = {
      date,
      isOpenHouse: true,
      listingId: listing.id,
      timezone: moment.tz.guess(),
    };

    this.props.dispatch(
      submitShowingRequest(
        values,
        token,
        () => this.handleSuccessRequest(),
        true,
      ),
    );
  };

  handleMultiSelect = () => {
    const calendarApi = this.calendarRef.current.getApi();
    calendarApi.unselect();
  };

  render() {
    const {
      classes,
      containerHeight,
      disableSelect,
      openHouses,
      showings,
    } = this.props;
    const events = frameCalendarEvents(classes, showings);
    const openHouseEvents = frameOpenHouseEvents(classes, openHouses);
    return (
      <React.Fragment>
        <FullCalendar
          ref={this.calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          handleWindowResize
          stickyHeaderDates
          eventStartEditable={false}
          dragScroll={false}
          allDaySlot={false}
          aspectRatio={1.8}
          slotMinTime="05:00:00"
          slotMaxTime="22:00:00"
          longPressDelay={1} // For selecting on Mobile devices
          headerToolbar={{
            left: 'prev,next',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          businessHours={{
            daysOfWeek: [0, 1, 2, 3, 4, 5, 6, 7], // Sunday - Saturday
            startTime: '5:00', // a start time (6am)
            endTime: '22:00', // an end time (10pm)
            allDay: false,
          }}
          views={{
            dayGridMonth: {
              titleFormat: { year: 'numeric', month: 'short' },
            },
            timeGridWeek: {
              titleFormat: { month: 'short', day: '2-digit' },
            },
            timeGridDay: {
              titleFormat: { month: 'short', day: '2-digit' },
            },
          }}
          validRange={{
            start: StartDate,
            end: MaxDate,
          }}
          selectable={!disableSelect}
          events={[...events, ...openHouseEvents]}
          select={this.handleDateClick}
          eventClick={this.handleEventClick}
          height={containerHeight ? containerHeight : 'auto'}
          selectAllow={dates => {
            const dayDiff = moment(dates.end).diff(moment(dates.start), 'days');
            // Making sure user is allowed to click only one date
            if (dayDiff === 0 || dayDiff === 1) {
              // Restricting user from selecting old dates
              const isOldDate = moment(dates.start).diff(moment(), 'days') < 0;
              if (isOldDate) {
                return false;
              }
              return true;
            } else {
              return false;
            }
          }}
        />
        <TourDialog
          open={this.state.openTourDialog}
          selectedDate={this.state.selectedDate}
          onClose={this.handleClose}
          scheduleShowing={this.handleScheduleShowing}
          updateShowing={this.handleUpdateShowing}
          deleteShowing={this.handleDeleteShowing}
          isEditing={this.state.isEditing}
        />
        <OpenHouseDialog
          open={this.state.displayOpenHouseDialog}
          onClose={this.handleClose}
          selectedDate={this.state.selectedDate}
          scheduleShowing={this.handleOpenHouseVisit}
          deleteShowing={this.handleDeleteShowing}
          isEditing={this.state.isEditing}
          selectedOpenHouse={this.state.selectedOpenHouse}
        />
        <ShowingsNotification />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: any) => ({
  showings: state.showings.showings,
  token: state.auth.token,
});

export default withStyles(styles)(connect(mapStateToProps)(ShowingsCalendar));
